import React, { useState, useEffect, useCallback, useMemo } from "react";
import confetti from "canvas-confetti";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Menu,
  X,
  MoreVertical,
  Eye,
  Calendar,
  Clock,
  MapPin,
  SlidersHorizontal,
} from "lucide-react";
import BookingActionsModal from "./BookingActionsModal";
import { Constants } from "../../../utils/constants";
import HActionButton from "../../../hcomponents/HActionButton";
// import HToggle from '../../../hcomponents/HToggle'; // Hidden for now
import HHeader from "../../../hcomponents/HHeader";
import HDayPopover from "../../../hcomponents/HDayPopover";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HDropdown from "../../../hcomponents/HDropdown";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../shadcn/ui/dropdown-menu";
import { Input } from "../../../shadcn/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../shadcn/ui/tooltip";
import SortDropdown from "../../../components/SortDropdown";
import { calendarService } from "../../../data/host-dummy-data/calendar/calendarService";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import * as localService from "../../../services/localService";
import { useServiceCategories } from "../../../hooks/useServiceCategories";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import { convertToUTC, getCurrentTimezoneOffset } from "../../../utils/timezoneUtils";
import { getBookingStatusBadgeClass, getBookingStatusLabel } from "../../../utils/bookingStatusUtils";
// usePagination hook available if needed
import useBookingFlow from "../../../hooks/useBookingFlow";
import { generateSpaceName, generateLocationString } from "../../../utils/spaceNamingUtils";
import { cn } from "../../../lib/utils";
import { AnalyticsEvents, AnalyticsProps, trackAnalyticsEvent } from "../../../utils/analytics";

import toastUtils from "../../../utils/toast";
import {
  getNormalizedDurationType,
  buildBookingTimeline,
  isDateBooked as timelineDateBooked,
} from "../../../utils/bookingTimeframeUtils";
import { showApiMessage } from "../../../utils/messageHandler";
import { canModify } from "../../../utils/bookingActionsUtils";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
// import HBookingModal from "../../../hcomponents/HBookingModal"; // Deprecated
import ReusableBookingModal from "../../../components/ReusableBookingModal";
import ModifyBookingModal from "./ModifyBookingModal";
import HUpgradeButton from "../../../hcomponents/HUpgradeButton";
import BookingActionsCard from "./BookingActionsCard";
import ConfirmationModal from "./ConfirmationModal";
import SpaceDetailsModal from "./components/SpaceDetailsModal";
import useBookingWebSocket from "../../../hooks/useBookingWebSocket";
import logger from "../../../utils/logger";


const BookingCard = ({ booking, onManage, onCancel, onCheckIn, onCheckOut, formatTimeRange, showConfirmation }) => {
  const [showActionsModal, setShowActionsModal] = useState(false);

  const start = new Date(booking.startTime);
  const end = new Date(booking.endTime);

  const formatDate = date => date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  const formatTime = date => date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

  const guestName = booking.name || "Guest";
  const guestEmail = booking.email || "";
  const guestAvatar =
    booking.avatar ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(guestName)}&background=f3f4f6&color=374151&size=80`;

  const handleManageClick = () => {
    if (onManage) {
      setShowActionsModal(true);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all">
        {/* Desktop Row Layout */}
        <div className="hidden lg:flex items-center px-4 py-3 gap-4 overflow-x-auto">
          {/* Booking ID */}
          <div className="flex-shrink-0 w-16 xl:w-20">
            <span className="text-xs font-mono text-gray-400">#{booking.id}</span>
          </div>

          {/* Space Title */}
          <div className="flex-shrink-0 w-36 xl:w-48 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{booking.title || `Booking #${booking.id}`}</p>
          </div>

          {/* Date & Time */}
          <div className="flex-shrink-0 w-40 xl:w-44">
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              <span>{formatDate(start)}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
              <Clock className="w-3 h-3 text-gray-400" />
              <span>
                {formatTime(start)} – {formatTime(end)}
              </span>
            </div>
          </div>

          {/* Location */}
          <div className="flex-1 min-w-0">
            {booking.location && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="truncate">{booking.location}</span>
              </div>
            )}
          </div>

          {/* Guest */}
          <div className="flex-shrink-0 w-36 xl:w-40 min-w-0">
            <div className="flex items-center gap-2">
              <img
                src={guestAvatar}
                alt={guestName}
                className="w-6 h-6 rounded-full object-cover bg-gray-100 flex-shrink-0"
                onError={e => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(guestName)}&background=f3f4f6&color=374151&size=80`;
                }}
              />
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-800 truncate">{guestName}</p>
                {booking.username ? (
                  <p className="text-[10px] text-gray-500 truncate lowercase">{booking.username.replace("@", "")}</p>
                ) : (
                  guestEmail && <p className="text-[10px] text-gray-500 truncate">{guestEmail}</p>
                )}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex-shrink-0 w-24 xl:w-28 min-w-0">
            <span
              className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getBookingStatusBadgeClass(booking.status)}`}
            >
              {getBookingStatusLabel(booking.status)}
            </span>
          </div>

          {/* Action */}
          <div className="flex-shrink-0 w-20 xl:w-24 flex justify-end">
            <button
              type="button"
              onClick={handleManageClick}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Manage
            </button>
          </div>
        </div>

        {/* Mobile Card Layout */}
        <div className="lg:hidden p-3">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{booking.title || `Booking #${booking.id}`}</p>
              <span className="text-xs font-mono text-gray-400">#{booking.id}</span>
            </div>
            <span
              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${getBookingStatusBadgeClass(booking.status)}`}
            >
              {getBookingStatusLabel(booking.status)}
            </span>
          </div>

          {/* Date & Time Row */}
          <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3 text-gray-400" />
              {formatDate(start)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-gray-400" />
              {formatTime(start)} – {formatTime(end)}
            </span>
          </div>

          {/* Location */}
          {booking.location && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
              <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="truncate">{booking.location}</span>
            </div>
          )}

          {/* Guest & Action Row */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <img
                src={guestAvatar}
                alt={guestName}
                className="w-5 h-5 rounded-full object-cover bg-gray-100"
                onError={e => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(guestName)}&background=f3f4f6&color=374151&size=80`;
                }}
              />
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-medium text-gray-700 truncate">{guestName}</span>
                {booking.username && (
                  <span className="text-[10px] text-gray-500 truncate lowercase">
                    {booking.username.replace("@", "")}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleManageClick}
              className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Manage
            </button>
          </div>
        </div>
      </div>

      {/* Booking Actions Modal */}
      <BookingActionsModal
        isOpen={showActionsModal}
        onClose={() => setShowActionsModal(false)}
        booking={booking}
        onModify={onManage}
        onCancel={onCancel}
        onCheckIn={onCheckIn}
        onCheckOut={onCheckOut}
        formatTimeRange={formatTimeRange}
        showConfirmation={showConfirmation}
      />
    </>
  );
};

const HostCalendar = () => {
  const history = useHistory();
  const featureFlags = useSelector(state => state.subscription?.featureFlags || {});
  const canViewCalendar = !!featureFlags?.CALENDAR_VIEW_OF_BOOKINGS;
  const canBookForCustomer = !!featureFlags?.BOOKING_FOR_CUSTOMER;

  // Load service categories for category filtering
  const { serviceCategories } = useServiceCategories();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("List");
  const [_showViewDropdown, _setShowViewDropdown] = useState(false);
  const [_workspaceToggle, _setWorkspaceToggle] = useState(true); // Hidden for now
  const [_spacesFromBookings, setSpacesFromBookings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(null); // null (all), WORK, STAY, PLAY
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [listBookings, setListBookings] = useState([]);
  const [listTotalCount, setListTotalCount] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listLoading, setListLoading] = useState(true);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listSort, setListSort] = useState("createdAt:desc");
  const [listStatusFilter, setListStatusFilter] = useState("ALL");

  // WebSocket for real-time booking updates
  useBookingWebSocket(update => {
    setBookings(prev =>
      prev.map(b => (String(b.id) === String(update.bookingId) ? { ...b, status: update.newStatus } : b))
    );
    setListBookings(prev =>
      prev.map(b => (String(b.id) === String(update.bookingId) ? { ...b, status: update.newStatus } : b))
    );
  });
  const [showListFilters, setShowListFilters] = useState(false);
  const [_statistics, setStatistics] = useState(null);

  const LIST_SORT_OPTIONS = [
    { field: "createdAt", label: "Booked at" },
    { field: "startTime", label: "Upcoming" },
  ];

  // Get current user for feature restrictions
  const currentUser = localService.getUser();

  // Use centralized feature access control
  const canCreateBooking = FeatureAccess.canCreateBooking(currentUser);

  // Use centralized booking flow hook
  const { bookingError, errorMessage, hasActiveSubscription, handleBookNow, handlePayNow, resetBookingError } =
    useBookingFlow();

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [bookingForConfirmation, setBookingForConfirmation] = useState(null);
  const [availableTimeSlotsForModal, setAvailableTimeSlotsForModal] = useState([]);
  const [timeFormat, setTimeFormat] = useState("24h"); // Default to 24h
  const [showSpaceDetailsModal, setShowSpaceDetailsModal] = useState(false);
  const [selectedSpaceForDetails, setSelectedSpaceForDetails] = useState(null);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState({}); // Track which workspace menu is open

  const getSpaceImages = useCallback(space => {
    const images = [];

    const pushImage = source => {
      if (!source) return;
      if (typeof source === "string") {
        images.push(source);
      } else if (typeof source === "object") {
        images.push(source?.url || source?.resourceUrl || source?.link);
      }
    };

    if (Array.isArray(space?.media?.images) && space.media.images.length > 0) {
      space.media.images.forEach(pushImage);
    } else if (Array.isArray(space?.images) && space.images.length > 0) {
      space.images.forEach(pushImage);
    } else if (space?.image) {
      pushImage(space.image);
    }

    return images.filter(Boolean);
  }, []);

  // Helper to get category ID from category code
  const getCategoryIdFromCode = useCallback(
    categoryCode => {
      if (!categoryCode || !serviceCategories || serviceCategories.length === 0) {
        return null;
      }
      const normalizedCode = categoryCode.toUpperCase();
      const category = serviceCategories.find(
        cat => cat.code?.toUpperCase() === normalizedCode || cat.name?.toUpperCase() === normalizedCode
      );
      return category?.id || null;
    },
    [serviceCategories]
  );
  useEffect(() => {
    if (!canViewCalendar && viewMode !== "List") {
      setViewMode("List");
    }
  }, [canViewCalendar, viewMode]);

  const goToUpgrade = useCallback(() => {
    history.push("/host/pricing?view=packages");
  }, [history]);

  const showCalendarUpgradeNotice = useCallback(() => {
    toastUtils.info("Upgrade for calendar access", {
      action: {
        label: "View plans",
        onClick: goToUpgrade,
      },
    });
  }, [goToUpgrade]);

  const selectedWorkspaceDuration = useMemo(() => getNormalizedDurationType(selectedWorkspace), [selectedWorkspace]);

  const bookingsForSelectedWorkspace = useMemo(() => {
    if (!selectedWorkspace || !bookings || bookings.length === 0) return [];
    return bookings.filter(booking => {
      if (!booking?.spaceId) return false;
      return Number(booking.spaceId) === Number(selectedWorkspace.id);
    });
  }, [bookings, selectedWorkspace]);

  const selectedWorkspaceTimeline = useMemo(
    () => buildBookingTimeline(bookingsForSelectedWorkspace),
    [bookingsForSelectedWorkspace]
  );

  const changeViewMode = useCallback(
    mode => {
      // List view is always available, calendar views require subscription
      if (!canViewCalendar && mode !== "List") {
        showCalendarUpgradeNotice();
        return;
      }
      setViewMode(mode);
    },
    [canViewCalendar, showCalendarUpgradeNotice]
  );

  const collectBookingImages = apiBooking => {
    const images = [];
    const pushImage = src => {
      if (!src) return;
      if (typeof src === "string") images.push(src);
      if (src?.url) images.push(src.url);
      if (src?.resourceUrl) images.push(src.resourceUrl);
      if (src?.link) images.push(src.link);
    };

    // Prefer media at the booking/space level; avoid building address fallbacks to keep space visuals
    apiBooking?.media?.images?.forEach(pushImage);
    apiBooking?.spaceInstance?.media?.images?.forEach(pushImage);
    apiBooking?.space?.media?.images?.forEach(pushImage);

    return images.filter(Boolean);
  };

  // Helper function to transform API booking data to calendar format
  const transformBookingData = useCallback(apiBooking => {
    const startTime = new Date(apiBooking.startTime);
    const endTime = new Date(apiBooking.endTime);
    const date = new Date(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());

    // Extract details from nested objects
    const spaceInstance = apiBooking.spaceInstance || {};
    const locationObj = spaceInstance.location || {};
    const building = locationObj.buildingAddress || {};
    const bookedBy = apiBooking.bookedBy || {};
    const images = collectBookingImages(apiBooking);

    // Handle case where bookedBy might be null
    const guestName = bookedBy
      ? bookedBy.firstName
        ? `${bookedBy.firstName} ${bookedBy.lastName || ""}`.trim()
        : bookedBy.username || "Guest"
      : "Guest";

    // Get instance display name
    const getInstanceDisplayName = instance => {
      if (!instance) return null;
      if (instance.instanceNumber) return `#${instance.instanceNumber}`;
      if (instance.title) return instance.title;
      if (instance.id) return `#${instance.id}`;
      return null;
    };

    const instanceDisplayName = getInstanceDisplayName(spaceInstance);

    return {
      id: apiBooking.id,
      date,
      startTime,
      endTime,
      name: guestName,
      email: bookedBy?.email || bookedBy?.emailAddress || "",
      username: bookedBy?.username || "",
      avatar:
        bookedBy?.imageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(guestName)}&background=random`,
      spaceId: apiBooking.spaceId, // Use spaceId for filtering
      workspaceId: apiBooking.spaceId, // Keep workspaceId for backwards compatibility
      spaceInstanceId: apiBooking.spaceInstanceId,
      instanceDisplayName: instanceDisplayName, // Instance number/title for display
      instanceNumber: spaceInstance.instanceNumber, // Instance number if available
      notes: apiBooking.notes || "",
      emails: apiBooking.emails || [],
      status: apiBooking.status,
      createdAt: apiBooking.createdAt,
      updatedAt: apiBooking.updatedAt,
      // For display in cards
      title: spaceInstance.title || `Booking #${apiBooking.id}`,
      location: building?.streetAddress
        ? `${building.streetAddress}, ${building.city || ""}, ${building.region || ""}, ${building.country || ""}`
        : locationObj?.name || spaceInstance.title || "",
      image: images[0] || "/assets/img/Web/Spacia/imgplaceholder.png",
      images,
      bookingDate: startTime,
      host: bookedBy?.firstName || guestName,
      // Store original booking data for space extraction
      apiBooking: apiBooking,
    };
  }, []);

  // Helper function to fetch all bookings across all pages
  const fetchAllBookings = useCallback(async timezone => {
    try {
      const bookingsResponse = await spaceManagementService.searchBookings({ pageNumber: 0, pageSize: 1000 }, timezone);

      if (bookingsResponse.status === 200 && bookingsResponse.data.content) {
        return bookingsResponse.data.content;
      }
    } catch (error) {
      logger.error("🔄 [CALENDAR] Error fetching bookings:", error);
    }
    return [];
  }, []);

  // Create a loadBookings function that can be reused
  const loadBookings = useCallback(async () => {
    try {
      logger.log("🔄 [CALENDAR] loadBookings function called");
      const currentUser = localService.getUser();
      let userId = null;

      if (currentUser) {
        userId = currentUser.id;
        logger.log("🔄 [CALENDAR] Found user ID:", userId);
      } else {
        logger.warn("🔄 [CALENDAR] No current user found");
        return;
      }

      if (!userId) {
        logger.warn("🔄 [CALENDAR] No userId available, skipping bookings fetch");
        return;
      }

      logger.log("🔄 [CALENDAR] Fetching all bookings for userId:", userId);
      const timezone = getCurrentTimezoneOffset();

      // Fetch all bookings across all pages
      const allBookings = await fetchAllBookings(timezone);
      logger.log("🔄 [CALENDAR] Total bookings fetched:", allBookings.length);

      const transformedBookings = allBookings.map(transformBookingData);
      setBookings(transformedBookings);

      const sortedList = [...transformedBookings].sort(
        (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      );
      setListBookings(sortedList.slice(0, 20));
      setListTotalCount(sortedList.length);
    } catch (error) {
      logger.error("🔄 [CALENDAR] Error loading bookings:", error);
    }
  }, [fetchAllBookings, transformBookingData]);

  const fetchListBookings = useCallback(
    async (page = 1, searchTerm = searchQuery, sort = listSort, status = listStatusFilter) => {
      const timezone = getCurrentTimezoneOffset();
      const [field = "startTime", direction = "asc"] = (sort || "startTime:asc").split(":");
      const sortParam = `${field},${direction}`;
      setListLoading(true);
      try {
        const queryParams = {
          pageNumber: page - 1,
          pageSize: 20,
          sort: sortParam,
          search: searchTerm || undefined,
        };

        if (status !== "ALL") {
          queryParams.status = status.toUpperCase();
        }

        const res = await spaceManagementService.searchBookings(queryParams, timezone);

        if (res?.status === 200) {
          const content = res.data?.content || [];
          setListBookings(content.map(transformBookingData));
          setListTotalCount(res.data?.totalElements ?? res.data?.total ?? content.length);
          setListPage(page);
        }
      } catch (err) {
        logger.error("Failed to fetch list bookings:", err);
        showApiMessage(err, "fetch bookings");
      } finally {
        setListLoading(false);
      }
    },
    [listSort, searchQuery, listStatusFilter, transformBookingData]
  );

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      const startTime = performance.now();
      try {
        logger.log("🚀 [LOAD] Starting initial data load...");
        setLoading(true);

        // Get current user from localService
        const currentUser = localService.getUser();
        let userId = null;

        if (currentUser) {
          userId = currentUser.id;
          logger.log("Extracted userId:", userId);
          logger.log("Current user:", currentUser);
        }

        // Load user's spaces with availability data and bookings separately
        // Load all spaces with pagination - only get published spaces
        // Apply category filter if selected
        let allSpaces = [];
        if (userId) {
          let page = 0;
          let hasMore = true;

          // Get categoryId if category filter is selected
          const categoryId = getCategoryIdFromCode(categoryFilter);

          while (hasMore) {
            const searchParams = {
              pageSize: 100,
              pageNumber: page,
              status: "PUBLISHED",
            };

            // Add categoryId filter if a category is selected
            if (categoryId) {
              searchParams.categoryId = categoryId;
              logger.log("🔍 [CALENDAR] Filtering by category:", categoryFilter, "categoryId:", categoryId);
            }

            const response = await spaceManagementService.searchSpaces(searchParams);
            if (response?.data?.content) {
              allSpaces = [...allSpaces, ...response.data.content];
              hasMore = response.data.content.length === 100;
              page++;
            } else {
              hasMore = false;
            }
          }
        } else {
          const workspacesResp = await calendarService.getWorkspaces();
          allSpaces = workspacesResp?.data || [];
        }

        const spacesResponse = { status: 200, data: { content: allSpaces } };

        // Debug logging for bookings API call
        logger.log("🔍 [CALENDAR] About to fetch bookings with userId:", userId);
        logger.log("🔍 [CALENDAR] User object:", currentUser);

        let allBookingsData = [];
        if (userId) {
          const timezone = getCurrentTimezoneOffset();
          // Fetch all bookings across all pages for host's spaces (including other users' bookings)
          logger.log("🔍 [CALENDAR] Fetching all bookings...");
          allBookingsData = await fetchAllBookings(timezone);
          logger.log("🔍 [CALENDAR] Total bookings fetched:", allBookingsData.length);
        } else {
          logger.warn("🔍 [CALENDAR] No userId available, skipping bookings fetch");
        }

        // Load statistics (fallback to dummy data for now)
        let statsResponse;
        try {
          statsResponse = await calendarService.getCalendarStatistics();
        } catch (error) {
          logger.warn("Calendar statistics not available, using default");
          statsResponse = {
            status: 200,
            data: { totalBookings: 0, upcomingBookings: 0 },
          };
        }

        // Process spaces response - ensure workspaces is always an array
        let processedWorkspaces = [];
        logger.log("Spaces response:", spacesResponse);

        if (spacesResponse && spacesResponse.status === 200) {
          if (spacesResponse.data && spacesResponse.data.content && Array.isArray(spacesResponse.data.content)) {
            // SearchSpaces API response - transform spaces to workspace format with availability
            processedWorkspaces = spacesResponse.data.content.map(space => {
              const spaceImages = getSpaceImages(space);
              // Get location info from first instance if available
              const firstInstance = space.instances?.[0];

              return {
                id: space.id,
                name: generateSpaceName(space, firstInstance),
                type: "workspace",
                capacity: space.capacity || "N/A",
                location: generateLocationString(space, firstInstance),
                image: spaceImages[0] || "/img/placeholder-workspace.jpg",
                images: spaceImages, // Store all images for carousel
                availableSlots: space.availableSlots || [], // This is the key difference - availability data!
                spaceType: space.spaceType,
                category: space.category,
                subCategory: space.subCategory || null, // Add subcategory for booking type determination
                duration: space.duration || "HOURLY", // Add duration type (HOURLY, DAILY, WEEKLY, MONTHLY)
                ratePrice: space.ratePrice,
                currency: space.currency,
                instances: space.instances || [],
                minimumDuration: space.minimumDuration,
                maximumDuration: space.maximumDuration,
                bufferTimeInMinutes: space.bufferTimeInMinutes || 0,
                tickets: space.tickets || [], // Add tickets for play services
                description: space.description || "", // Add description for details modal
                amenities: space.amenities || [], // Add amenities for details modal
              };
            });
          } else if (spacesResponse.data && spacesResponse.data.data && Array.isArray(spacesResponse.data.data)) {
            // Fallback for getUserSpaces response format
            processedWorkspaces = spacesResponse.data.data.map(space => {
              const spaceImages = getSpaceImages(space);
              const firstInstance = space.instances?.[0];
              return {
                id: space.id,
                name: generateSpaceName(space, firstInstance),
                type: "workspace",
                capacity: space.details?.capacity || space.details?.maxCapacity || space.capacity || "N/A",
                location: generateLocationString(space, firstInstance),
                image: spaceImages[0] || "/img/placeholder-workspace.jpg",
                images: spaceImages,
                availableSlots: space.availableSlots || [],
                category: space.category,
                subCategory: space.subCategory || null, // Add subcategory for booking type determination
                duration: space.duration || "HOURLY", // Add duration type (HOURLY, DAILY, WEEKLY, MONTHLY)
                minimumDuration: space.minimumDuration,
                maximumDuration: space.maximumDuration,
                bufferTimeInMinutes: space.bufferTimeInMinutes || 0,
                instances: space.instances || [],
                totalInstances: space.instances?.length || space.numberOfInstances || 1,
                description: space.description || "", // Add description for details modal
                amenities: space.amenities || [], // Add amenities for details modal
              };
            });
          } else if (Array.isArray(spacesResponse.data)) {
            // Fallback to dummy data
            processedWorkspaces = spacesResponse.data;
          } else {
            logger.warn("Unexpected spaces response format:", spacesResponse.data);
            processedWorkspaces = [];
          }
        } else {
          logger.warn("Failed to load spaces:", spacesResponse);
          processedWorkspaces = [];
        }

        logger.log("Processed workspaces:", processedWorkspaces);
        logger.log(
          "Spaces with availability:",
          processedWorkspaces.filter(s => s.availableSlots && s.availableSlots.length > 0)
        );

        // Don't auto-select workspace - let users choose to filter
        setSelectedWorkspace(null); // Start with no filter

        // Process bookings data
        let processedBookings = [];
        if (allBookingsData && Array.isArray(allBookingsData) && allBookingsData.length > 0) {
          // Transform all bookings
          processedBookings = allBookingsData.map(transformBookingData);
          logger.log("🔍 [CALENDAR] Processed bookings:", processedBookings.length);
        } else {
          logger.log("🔍 [CALENDAR] No bookings to process");
          processedBookings = [];
        }

        setBookings(processedBookings);

        // Extract unique spaces from bookings for reference (but we now get spaces from searchSpaces API)
        logger.log("Processing bookings, booking count:", processedBookings.length);
        const uniqueSpaces = new Map();

        processedBookings.forEach(booking => {
          if (booking.spaceId) {
            const spaceInstance = booking.apiBooking?.spaceInstance || {};
            const locationObj = spaceInstance.location || {};
            const building = locationObj.buildingAddress || {};

            const spaceData = {
              id: booking.spaceId,
              name: spaceInstance.title || locationObj.name || `Space ${booking.spaceId}`,
              type: "workspace",
              capacity: "N/A",
              location: building.streetAddress
                ? `${building.streetAddress}, ${building.city || ""}`
                : locationObj.name || "Location not specified",
              image: building.images?.[0] || "/img/placeholder-workspace.jpg",
              availableSlots: spaceInstance.availabilitySlots || [],
              totalInstances: spaceInstance.instances?.length || spaceInstance.numberOfInstances || 1,
            };

            if (!uniqueSpaces.has(booking.spaceId)) {
              uniqueSpaces.set(booking.spaceId, spaceData);
            }
          }
        });

        const spacesFromBookingsArray = Array.from(uniqueSpaces.values());
        logger.log("Spaces found in bookings:", spacesFromBookingsArray);
        setSpacesFromBookings(spacesFromBookingsArray);

        // We strictly use processedWorkspaces (which are filtered by PUBLISHED status)
        // and no longer supplement with spaces from bookings to avoid showing unpublished spaces.
        const combinedSpaces = [...processedWorkspaces];

        logger.log("Final combined spaces:", combinedSpaces);
        logger.log(
          "Spaces with availability:",
          combinedSpaces.filter(s => s.availableSlots && s.availableSlots.length > 0)
        );
        setWorkspaces(combinedSpaces);

        if (statsResponse && statsResponse.status === 200) {
          setStatistics(statsResponse.data);
        }

        const endTime = performance.now();
        logger.log(`🚀 [LOAD] Data loading completed successfully in ${(endTime - startTime).toFixed(2)}ms`);
        setLoading(false);
      } catch (error) {
        const endTime = performance.now();
        logger.error(`🚀 [LOAD] Error loading calendar data after ${(endTime - startTime).toFixed(2)}ms:`, error);
        setLoading(false);
        setWorkspaces([]); // Ensure workspaces is always an array
        setBookings([]); // Ensure bookings is always an array
        showApiMessage(error, "load calendar data");
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter, getCategoryIdFromCode]); // Refetch when category filter changes

  useEffect(() => {
    // Keep list view in sync with search, sort, and status filter
    setListPage(1);
    fetchListBookings(1, searchQuery, listSort, listStatusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, listSort, listStatusFilter]);

  useEffect(() => {
    fetchListBookings(listPage, searchQuery, listSort, listStatusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPage]);

  // Set initial date to today (only on first load)
  useEffect(() => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentDate(today);
    // Only run this effect once on mount
    // eslint-disable-next-line
  }, []);

  // Clear workspace filter when workspaces change (only on initial load)
  useEffect(() => {
    if (workspaces.length > 0 && !selectedWorkspace) {
      // Only clear if there's no selected workspace already
    }
  }, [workspaces, selectedWorkspace]);

  // Update availability when selected date or workspace changes (moved after function definition)

  // Note: Search filtering is now handled in the UI rendering section where workspaces are filtered

  const goToPreviousDay = () => {
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    setSelectedDate(prevDay);
  };

  const goToNextDay = () => {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    setSelectedDate(nextDay);
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentDate(today);
  };

  // Helper function to check if a date is available for the selected workspace
  const isDateAvailable = useCallback(
    (date, workspace = selectedWorkspace) => {
      if (!workspace?.availableSlots || workspace.availableSlots.length === 0) {
        return false;
      }

      const dayName = getDayNameFromDate(date);
      return workspace.availableSlots.some(slot => slot.day === dayName);
    },
    [selectedWorkspace]
  );

  const formatTimeRange = (startTime, endTime) => {
    const start = startTime
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
    const end = endTime
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
    return `${start} - ${end}`;
  };

  const formatDate = date => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const toggleMobileSidebar = () => {
    setShowMobileSidebar(!showMobileSidebar);
  };

  // Helper function to get day name from date
  const getDayNameFromDate = date => {
    const dayNames = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    return dayNames[date.getDay()];
  };

  // Helper function to get availability for a specific date and space
  // Returns ALL availability slots for that day (not just the first one)
  const getSpaceAvailabilityForDate = useCallback((space, date) => {
    if (!space?.availableSlots || space.availableSlots.length === 0) {
      return null;
    }

    const dayName = getDayNameFromDate(date);

    // Get ALL slots for this day (not just the first one)
    const availabilitySlotsForDay = space.availableSlots.filter(slot => slot.day === dayName);

    if (!availabilitySlotsForDay || availabilitySlotsForDay.length === 0) {
      return null;
    }

    // Convert time strings (HH:MM:SS format from API) to hours for easier comparison
    const parseTime = timeStr => {
      // Handle both "HH:MM:SS" and "HH:MM" formats
      const parts = timeStr.split(":");
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      return hours + minutes / 60;
    };

    // Convert HH:MM:SS to HH:MM for display
    const formatTimeForDisplay = timeStr => {
      const parts = timeStr.split(":");
      return `${parts[0]}:${parts[1]}`;
    };

    // Return array of all availability slots for this day
    return availabilitySlotsForDay.map(slot => ({
      day: dayName,
      startHour: parseTime(slot.startTime),
      endHour: parseTime(slot.endTime),
      startTime: formatTimeForDisplay(slot.startTime),
      endTime: formatTimeForDisplay(slot.endTime),
      rawStartTime: slot.startTime,
      rawEndTime: slot.endTime,
      // Include space constraints for booking validation
      minimumDuration: space.minimumDuration || 1,
      maximumDuration: space.maximumDuration || 24,
      duration: space.duration || "HOURLY",
      bufferTimeInMinutes: space.bufferTimeInMinutes || 0,
    }));
  }, []);

  // Helper function to generate available time slots for booking
  // Handles multiple availability slots per day and respects minimum/maximum duration
  const generateAvailableTimeSlots = (availabilityArray, space) => {
    if (!availabilityArray || (Array.isArray(availabilityArray) && availabilityArray.length === 0)) return [];

    // Handle both single availability object (backward compat) and array
    const availabilities = Array.isArray(availabilityArray) ? availabilityArray : [availabilityArray];

    // Get constraints from first availability slot or space object
    const firstAvail = availabilities[0];
    const minimumDuration = firstAvail?.minimumDuration || space?.minimumDuration || 1;
    const maximumDuration = firstAvail?.maximumDuration || space?.maximumDuration || 24;
    const duration = firstAvail?.duration || space?.duration || "HOURLY";
    const bufferTimeInMinutes = firstAvail?.bufferTimeInMinutes || space?.bufferTimeInMinutes || 0;

    // For HOURLY bookings, minimum duration must be at least 1 hour (60 minutes)
    const minDurationMinutes = duration === "HOURLY" ? minimumDuration * 60 : minimumDuration * 60;

    const slots = [];
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();

    // Process each availability slot for the day
    availabilities.forEach(availability => {
      // Parse start and end times from availability
      const [startHours, startMinutes] = availability.startTime.split(":").map(Number);
      const [endHours, endMinutes] = availability.endTime.split(":").map(Number);

      let startTotalMinutes = startHours * 60 + startMinutes;
      let endTotalMinutes = endHours * 60 + endMinutes;

      // Check if slot goes overnight (end time < start time)
      if (endTotalMinutes < startTotalMinutes) {
        endTotalMinutes += 24 * 60; // Add 24 hours
      }

      // Calculate available duration for this slot
      const slotDurationMinutes = endTotalMinutes - startTotalMinutes;

      // Skip slots that are shorter than minimum duration
      if (slotDurationMinutes < minDurationMinutes) {
        return;
      }

      // Generate 30-minute interval slots
      const intervalMinutes = 30;

      for (let totalMinutes = startTotalMinutes; totalMinutes < endTotalMinutes; totalMinutes += intervalMinutes) {
        const slotHour = Math.floor(totalMinutes / 60);
        const slotMinute = totalMinutes % 60;

        const slotStart = new Date(selectedDate);
        slotStart.setHours(slotHour, slotMinute, 0, 0);

        const slotEnd = new Date(selectedDate);
        slotEnd.setHours(slotHour, slotMinute + intervalMinutes, 0, 0);

        // Skip past time slots for today
        if (isToday && slotEnd <= now) {
          continue;
        }

        // Check if this slot start time allows for minimum duration
        const remainingMinutes = endTotalMinutes - totalMinutes;
        if (remainingMinutes < minDurationMinutes) {
          // Not enough time left in this availability slot for minimum duration
          continue;
        }

        const formatSlotTime = date => {
          const h = date.getHours();
          const m = date.getMinutes();
          return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
        };

        slots.push({
          hour: slotHour,
          minute: slotMinute,
          startTime: slotStart,
          endTime: slotEnd,
          available: true, // All slots are available - backend will validate conflicts
          timeLabel: `${formatSlotTime(slotStart)} - ${formatSlotTime(slotEnd)}`,
          // Include constraints for booking validation
          minimumDuration,
          maximumDuration,
          duration,
          bufferTimeInMinutes,
        });
      }
    });

    // Sort slots by start time
    slots.sort((a, b) => a.startTime - b.startTime);

    return slots;
  };

  // Handle workspace selection with availability
  const handleWorkspaceSelection = workspace => {
    setSelectedWorkspace(workspace);
  };

  const handleCreateBooking = () => {
    // Check if workspace is selected
    if (!selectedWorkspace) {
      toastUtils.info("Please select a workspace first", {
        description: "Choose a workspace from the sidebar to create a booking.",
      });
      return;
    }

    // Prepare available time slots for the selected workspace and date
    const availability = getSpaceAvailabilityForDate(selectedWorkspace, selectedDate);
    if (!availability || (Array.isArray(availability) && availability.length === 0)) {
      toastUtils.error("No availability for selected date", {
        description: `${selectedWorkspace.name} is not available on ${selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}.`,
      });
      return;
    }

    // Generate all time slots - backend will handle conflict validation
    // Pass both availability array and space object for constraints
    const availableSlots = generateAvailableTimeSlots(availability, selectedWorkspace);

    // Don't show modal if no time slots (only if outside availability hours or all past)
    if (!availableSlots || availableSlots.length === 0) {
      toastUtils.error("No available time slots", {
        description: `No time slots available for ${selectedWorkspace.name} on ${selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}.`,
      });
      return;
    }

    setAvailableTimeSlotsForModal(availableSlots);
    setIsEditMode(false);
    setCurrentBooking(null);
    trackAnalyticsEvent(AnalyticsEvents.HOST_CREATE_BOOKING_CLICKED, {
      [AnalyticsProps.LISTING_ID]: selectedWorkspace?.id,
      [AnalyticsProps.LISTING_NAME]: selectedWorkspace?.name,
    });
    setShowBookingModal(true);
  };

  const getExistingBookingsForSpace = useCallback(
    workspaceId => {
      if (!workspaceId || !bookings || bookings.length === 0) {
        return [];
      }
      return bookings.filter(
        booking => booking.spaceId === workspaceId || booking.spaceId?.toString() === workspaceId?.toString()
      );
    },
    [bookings]
  );

  // Memoized existing bookings for the modal based on selectedWorkspace
  const modalExistingBookings = useMemo(() => {
    if (!selectedWorkspace) return [];
    return getExistingBookingsForSpace(selectedWorkspace.id);
  }, [selectedWorkspace, getExistingBookingsForSpace]);

  // Stable handlers for booking modals
  const handleBookingModalClose = useCallback(() => {
    setShowBookingModal(false);
    resetBookingError();
  }, [resetBookingError]);

  const handleModifyModalClose = useCallback(() => {
    setShowBookingModal(false);
    setCurrentBooking(null);
    setIsEditMode(false);
  }, []);

  const handleBookingSuccess = useCallback(
    async (response, operation) => {
      await loadBookings();
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        colors: ["#6366f1", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"],
      });
    },
    [loadBookings]
  );

  const handleBookingError = useCallback((error, operation) => {
    showApiMessage(error, operation);
  }, []); // showApiMessage is stable from messageHandler

  const userCurrency = useMemo(() => localService.getUser()?.currency || "KES", []);

  // Handle creating booking from the modal
  const handleCreateBookingFromModal = async bookingData => {
    // Get current user info
    const currentUser = localService.getUser();
    let userId = null;

    if (currentUser) {
      userId = currentUser.id;
    }

    if (!userId) {
      toastUtils.error("Authentication required", {
        description: "Please log in to create a booking.",
      });
      return;
    }

    if (!bookingData.selectedTimeSlot) {
      toastUtils.error("Please select a time slot");
      return;
    }

    // Find the selected workspace - handle both string and number IDs
    const workspace = workspaces.find(w => w.id.toString() === bookingData.workspaceId.toString());
    if (!workspace) {
      logger.log(
        "Available workspaces:",
        workspaces.map(w => ({ id: w.id, name: w.name }))
      );
      logger.log("Looking for workspace ID:", bookingData.workspaceId);
      toastUtils.error("Selected workspace not found");
      return;
    }

    // Find the first available instance for the selected workspace
    let _spaceInstanceId = null;
    if (workspace.instances && workspace.instances.length > 0) {
      _spaceInstanceId = workspace.instances[0].id;
      logger.log("Using space instance:", _spaceInstanceId, "from", workspace.instances.length, "instances");
    } else {
      logger.warn("No instances found for workspace, using space ID as fallback");
      _spaceInstanceId = workspace.id; // Fallback to space ID
    }

    const bookingPayload = {
      userId: userId.toString(), // Convert to string as per API spec
      spaceId: workspace.id, // Use spaceId (not workspaceId)
      emails: canBookForCustomer ? bookingData.emails || [] : [],
      startTime: convertToUTC(bookingData.selectedTimeSlot.startTime),
      endTime: convertToUTC(bookingData.selectedTimeSlot.endTime),
      notes: bookingData.description || `Booking for ${workspace.name}`,
    };

    logger.log("Creating booking with payload:", bookingPayload);

    // Use centralized booking handler
    const result = await handleBookNow(bookingPayload, {
      onSuccess: async response => {
        showApiMessage(response, "create booking");

        // Close the modal
        setShowBookingModal(false);

        // Check if this is a play service with an OFFLINE ticket URL
        if (workspace.tickets && workspace.tickets.length > 0) {
          const firstTicket = workspace.tickets[0];
          if (firstTicket.ticketType !== "DEFAULT" && firstTicket.url) {
            let finalUrl = firstTicket.url;
            if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
              finalUrl = "https://" + finalUrl;
            }
            logger.log("Redirecting to ticket URL:", finalUrl);
            toastUtils.success("Booking successful! Redirecting to tickets...", {
              description: "You will be redirected to complete your ticket purchase.",
            });
            // Redirect after a short delay to allow the success message to be seen
            setTimeout(() => {
              window.open(finalUrl, "_blank");
            }, 1500);
          }
        }

        // Refresh bookings to show the new booking
        await loadBookings();
      },
      onError: error => {
        logger.error("Booking error:", error);
        // Use showApiMessage to show the exact backend message (handles 4xx errors)
        showApiMessage(error, "create booking");
        // Modal stays open so user can try payment option
      },
    });

    return result;
  };

  // Handle booking creation with pending payment
  const handleCreateBookingPendingPayment = async bookingData => {
    const userId = currentUser?.id;
    if (!userId) {
      toastUtils.error("Authentication required", {
        description: "Please log in to create a booking.",
      });
      return;
    }

    if (!bookingData.workspaceId) {
      toastUtils.error("Please select a workspace");
      return;
    }

    if (!bookingData.selectedTimeSlot) {
      toastUtils.error("Please select a time slot");
      return;
    }

    // Find the selected workspace
    const workspace = workspaces.find(w => w.id.toString() === bookingData.workspaceId.toString());
    if (!workspace) {
      toastUtils.error("Selected workspace not found");
      return;
    }

    // Find the first available instance for the selected workspace
    let _spaceInstanceId = null;
    if (workspace.instances && workspace.instances.length > 0) {
      _spaceInstanceId = workspace.instances[0].id;
    } else {
      _spaceInstanceId = workspace.id;
    }

    const bookingPayload = {
      userId: userId.toString(), // Convert to string as per API spec
      spaceId: workspace.id,
      emails: bookingData.emails || [],
      startTime: convertToUTC(bookingData.selectedTimeSlot.startTime),
      endTime: convertToUTC(bookingData.selectedTimeSlot.endTime),
      notes: bookingData.description || `Booking for ${workspace.name}`,
    };

    logger.log("Creating booking with pending payment:", bookingPayload);

    // Use centralized payment handler
    const result = await handlePayNow(bookingPayload, {
      space: workspace,
      onError: error => {
        logger.error("Error creating booking with pending payment:", error);
        // Use showApiMessage to show the exact backend message (handles 4xx errors)
        showApiMessage(error, "create booking");
      },
    });

    if (result?.success) {
      if (result?.isPlayService && result?.ticketUrl) {
        toastUtils.success("Booking created! Redirecting to tickets...", {
          description: "You will be redirected to complete your ticket purchase.",
        });
        setShowBookingModal(false);
        setTimeout(() => {
          window.open(result.ticketUrl, "_blank");
        }, 1500);
      } else {
        setShowBookingModal(false);
        if (result?.paymentResult?.success) {
          toastUtils.success("Payment initiated", {
            description: "Complete payment in the new tab to confirm your booking.",
          });
        }
      }

      await loadBookings();
    }

    return result;
  };

  // Handle booking creation from available time slot
  const _handleCreateBookingFromSlot = async slot => {
    if (!selectedWorkspace || !slot.available) return;

    try {
      // Get current user info
      const currentUser = localService.getUser();
      let userId = null;

      if (currentUser) {
        userId = currentUser.id;
        logger.log("Create booking from slot - Extracted userId:", userId);
      }

      if (!userId) {
        toastUtils.error("Authentication required", {
          description: "Please log in to create a booking.",
        });
        return;
      }

      const bookingPayload = {
        userId: userId.toString(), // Convert to string as per API spec
        spaceId: selectedWorkspace.id,
        startTime: convertToUTC(slot.startTime),
        endTime: convertToUTC(slot.endTime),
        notes: `Booking for ${selectedWorkspace?.name} - ${slot.timeLabel}`,
      };

      // Use centralized booking handler
      const shouldTryRegularBooking = hasActiveSubscription && !bookingError;
      const result = await (shouldTryRegularBooking ? handleBookNow : handlePayNow)(bookingPayload, {
        space: selectedWorkspace,
        onSuccess: async response => {
          showApiMessage(response, "create booking");

          // Check if this is a play service with an OFFLINE ticket URL
          if (selectedWorkspace?.tickets && selectedWorkspace.tickets.length > 0) {
            const firstTicket = selectedWorkspace.tickets[0];
            if (firstTicket.ticketType !== "DEFAULT" && firstTicket.url) {
              let finalUrl = firstTicket.url;
              if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
                finalUrl = "https://" + finalUrl;
              }
              logger.log("Redirecting to ticket URL:", finalUrl);
              toastUtils.success("Booking successful! Redirecting to tickets...", {
                description: "You will be redirected to complete your ticket purchase.",
              });
              setTimeout(() => {
                window.open(finalUrl, "_blank");
              }, 1500);
            }
          }

          // Refresh bookings to show the new booking
          await loadBookings();
        },
        onError: error => {
          logger.error("Error creating booking from slot:", error);
          // Use showApiMessage to show the exact backend message (handles 4xx errors)
          showApiMessage(error, "create booking");
        },
      });

      if (!shouldTryRegularBooking && result?.success && result?.isPlayService && result?.ticketUrl) {
        toastUtils.success("Booking created! Redirecting to tickets...", {
          description: "You will be redirected to complete your ticket purchase.",
        });
        setTimeout(() => {
          window.open(result.ticketUrl, "_blank");
        }, 1500);
      } else if (!shouldTryRegularBooking && result?.success && result?.paymentResult?.success) {
        toastUtils.success("Payment initiated", {
          description: "Complete payment in the new tab to confirm your booking.",
        });
      }

      return result;
    } catch (error) {
      logger.error("Error creating booking from slot:", error);

      // If regular booking failed, show error
      if (hasActiveSubscription && !bookingError) {
        toastUtils.error("Booking failed", {
          description: "Try using Pay button to complete your booking.",
        });
      } else {
        toastUtils.error("Booking failed", {
          description: "Please try again.",
        });
      }
    }
  };

  // Booking management handlers
  const handleModifyBooking = booking => {
    logger.log("Modify booking:", booking);

    // Validate that the booking can be modified before opening the modal
    if (!canModify(booking)) {
      const status = booking?.status?.toUpperCase();
      let message = "This booking cannot be modified.";

      if (status === "CHECKED_IN") {
        message = "Cannot edit booking after check-in. Please check out the guest first.";
      } else if (["CANCELED", "CANCELLED"].includes(status)) {
        message = "Cannot edit a cancelled booking.";
      } else if (status === "COMPLETED") {
        message = "Cannot edit a completed booking.";
      } else if (status === "NO_SHOW") {
        message = "Cannot edit a no-show booking.";
      } else if (status === "PENDING_PAYMENT") {
        message = "Cannot edit booking while payment is pending. Please complete or cancel the payment first.";
      }

      showApiMessage(
        {
          response: {
            status: 400,
            data: { message },
          },
        },
        "edit booking"
      );
      return;
    }

    // Generate available time slots for the booking's date
    const bookingDate = new Date(booking.startTime);

    const workspace = workspaces.find(w => w.id === booking.spaceId);
    const availability = workspace ? getSpaceAvailabilityForDate(workspace, bookingDate) : null;

    // Generate all slots - backend will handle conflict validation
    const slots = availability ? generateAvailableTimeSlots(availability, workspace) : [];

    setAvailableTimeSlotsForModal(slots);
    setSelectedDate(bookingDate);
    setSelectedWorkspace(workspace);
    setCurrentBooking(booking);
    setIsEditMode(true);
    setShowBookingModal(true);
  };

  const handleUpdateBookingFromModal = useCallback(
    async (bookingId, updatePayload) => {
      try {
        logger.log("Updating booking:", bookingId, updatePayload);

        const timezone = getCurrentTimezoneOffset();
        const response = await spaceManagementService.updateBooking(bookingId, updatePayload, timezone);

        if (response.status === 200 || response.status === 204) {
          showApiMessage(response, "update booking");
          setShowBookingModal(false);
          setCurrentBooking(null);
          setIsEditMode(false);

          // Refresh bookings
          await loadBookings();
        } else {
          showApiMessage(response, "update booking");
        }
      } catch (error) {
        logger.error("Error updating booking:", error);
        showApiMessage(error, "update booking");
      }
    },
    [loadBookings]
  );

  const handleCancelBooking = useCallback(
    async booking => {
      try {
        logger.log("Cancelling booking:", booking);

        const response = await spaceManagementService.cancelBooking(booking.id);

        if (response.status === 200 || response.status === 204) {
          showApiMessage(response, "cancel booking");

          // Refresh bookings
          await loadBookings();
        } else {
          showApiMessage(response, "cancel booking");
        }
      } catch (error) {
        logger.error("Error cancelling booking:", error);
        showApiMessage(error, "cancel booking");
      }
    },
    [loadBookings]
  );

  const handleCheckInBooking = useCallback(
    async booking => {
      try {
        logger.log("Checking in booking:", booking);

        const response = await spaceManagementService.checkInBooking(booking.id);

        if (response.status === 200 || response.status === 201) {
          showApiMessage(response, "check in");

          // Refresh bookings to update status
          await loadBookings();
        } else {
          showApiMessage(response, "check in");
        }
      } catch (error) {
        logger.error("Error checking in booking:", error);
        showApiMessage(error, "check in");
      }
    },
    [loadBookings]
  );

  const handleCheckOutBooking = useCallback(
    async booking => {
      try {
        logger.log("Checking out booking:", booking);

        const response = await spaceManagementService.checkOutBooking(booking.id);

        if (response.status === 200 || response.status === 201) {
          showApiMessage(response, "check out");

          // Refresh bookings to update status
          await loadBookings();
        } else {
          showApiMessage(response, "check out");
        }
      } catch (error) {
        logger.error("Error checking out booking:", error);
        showApiMessage(error, "check out");
      }
    },
    [loadBookings]
  );

  // Confirmation handlers
  const handleConfirmCheckIn = useCallback(async () => {
    if (bookingForConfirmation) {
      await handleCheckInBooking(bookingForConfirmation);
      setShowConfirmationModal(false);
      setBookingForConfirmation(null);
      setConfirmationAction(null);
    }
  }, [bookingForConfirmation, handleCheckInBooking]);

  const handleConfirmCheckOut = useCallback(async () => {
    if (bookingForConfirmation) {
      await handleCheckOutBooking(bookingForConfirmation);
      setShowConfirmationModal(false);
      setBookingForConfirmation(null);
      setConfirmationAction(null);
    }
  }, [bookingForConfirmation, handleCheckOutBooking]);

  const handleConfirmCancel = useCallback(async () => {
    if (bookingForConfirmation) {
      await handleCancelBooking(bookingForConfirmation);
      setShowConfirmationModal(false);
      setBookingForConfirmation(null);
      setConfirmationAction(null);
    }
  }, [bookingForConfirmation, handleCancelBooking]);

  const showConfirmation = useCallback((action, booking) => {
    setConfirmationAction(action);
    setBookingForConfirmation(booking);
    setShowConfirmationModal(true);
  }, []);

  const MiniCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const monthName = currentDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    const days = [];
    const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push({
        day: daysInPrevMonth - firstDayOfMonth + i + 1,
        month: prevMonth,
        year: prevYear,
        isCurrentMonth: false,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        month: month,
        year,
        isCurrentMonth: true,
      });
    }

    const remainingSlots = 42 - days.length;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;

    for (let i = 1; i <= remainingSlots; i++) {
      days.push({
        day: i,
        month: nextMonth,
        year: nextYear,
        isCurrentMonth: false,
      });
    }

    const isSelected = (day, month, year) => {
      return day === selectedDate.getDate() && month === selectedDate.getMonth() && year === selectedDate.getFullYear();
    };

    return (
      <div className="mb-2">
        <div className="flex justify-between items-center mb-1.5">
          <h3 className="text-sm font-medium text-gray-900">{monthName}</h3>
          <div className="flex space-x-1">
            <button
              onClick={() => setCurrentDate(new Date(year, month - 1))}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-3 h-3 text-gray-600" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date(year, month + 1))}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Next month"
            >
              <ChevronRight className="w-3 h-3 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Go to Today button */}
        <div className="mb-2">
          <button
            onClick={goToToday}
            className="px-2 py-1 w-full text-xs text-blue-600 bg-blue-50 rounded transition-colors hover:bg-blue-100"
          >
            Today
          </button>
        </div>

        <div className="grid grid-cols-7 mb-1 text-xs text-center text-gray-500">
          {daysOfWeek.map((day, index) => (
            <div key={index} className="py-1 font-medium">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((dayObj, index) => {
            const dayDate = new Date(dayObj.year, dayObj.month, dayObj.day);
            const isSelectedDay = isSelected(dayObj.day, dayObj.month, dayObj.year);
            const isCurrentDay = dayDate.toDateString() === new Date().toDateString();
            const isAvailable = selectedWorkspace && isDateAvailable(dayDate);
            const durationType = selectedWorkspaceDuration;

            // Pass category and duration type for proper DAILY booking validation
            const isBooked =
              selectedWorkspace &&
              timelineDateBooked(
                dayDate,
                selectedWorkspaceTimeline,
                selectedWorkspace.totalInstances || 1,
                durationType,
                selectedWorkspace.category
              );

            // Check if the date is in the past (before today)
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
            const isPastDate = dayDate < today;
            // ONLY past days should be unclickable
            const isDisabled = isPastDate;

            return (
              <button
                key={index}
                onClick={() => {
                  if (!isDisabled) {
                    setSelectedDate(new Date(dayObj.year, dayObj.month, dayObj.day));
                  }
                }}
                disabled={isDisabled}
                className={`
                   h-6 w-6 text-xs rounded-full flex items-center justify-center mx-auto font-medium transition-colors
                  ${
                    !dayObj.isCurrentMonth
                      ? "text-gray-300"
                      : isPastDate
                        ? "text-gray-300 bg-gray-100 cursor-not-allowed"
                        : isSelectedDay
                          ? "text-white"
                          : isBooked
                            ? "text-red-700 bg-red-100 hover:bg-red-200 border border-red-200"
                            : isCurrentDay
                              ? "text-purple-700 bg-purple-100 hover:bg-purple-200"
                              : isAvailable
                                ? "text-green-700 bg-green-100 hover:bg-green-200"
                                : "text-gray-700 hover:bg-gray-100"
                  }
                            `}
                style={isSelectedDay ? { backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR } : {}}
              >
                {dayObj.day}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper to normalize category to string
  const normalizeCategory = category => {
    if (!category) return null;
    if (typeof category === "string") return category.toUpperCase();
    if (typeof category === "object") {
      // Handle object with code or name property
      return (category.code || category.name || "").toUpperCase();
    }
    return String(category).toUpperCase();
  };

  // Helper to get category color and label
  const getCategoryInfo = category => {
    const normalized = normalizeCategory(category);
    switch (normalized) {
      case "WORK":
        return { label: "WORK", color: "bg-blue-100 text-blue-700 border-blue-200", dotColor: "bg-blue-500" };
      case "STAY":
        return { label: "STAY", color: "bg-green-100 text-green-700 border-green-200", dotColor: "bg-green-500" };
      case "PLAY":
        return { label: "PLAY", color: "bg-purple-100 text-purple-700 border-purple-200", dotColor: "bg-purple-500" };
      default:
        return { label: "OTHER", color: "bg-gray-100 text-gray-700 border-gray-200", dotColor: "bg-gray-500" };
    }
  };

  const WorkspaceItem = ({ workspace, isSelected, onClick, showCategory = true, onViewDetails }) => {
    const categoryInfo = getCategoryInfo(workspace.category);
    const [imageError, setImageError] = useState(false);
    const [tooltipImageIndex, setTooltipImageIndex] = useState(0);

    // Get all images for the workspace
    const workspaceImages = getSpaceImages(workspace);
    const hasMultipleImages = workspaceImages.length > 1;

    // Get image URL - handle both string and object formats
    const getImageUrl = () => {
      if (imageError) return null;
      if (!workspace.image) return null;
      if (typeof workspace.image === "string") return workspace.image;
      return workspace.image?.url || workspace.image?.resourceUrl || null;
    };

    const imageUrl = getImageUrl();

    // Reset image index when workspace changes
    useEffect(() => {
      setTooltipImageIndex(0);
    }, [workspace.id]);

    // Auto-slide tooltip carousel
    useEffect(() => {
      if (!hasMultipleImages || workspaceImages.length <= 1) return;

      const imageCount = workspaceImages.length;
      const interval = setInterval(() => {
        setTooltipImageIndex(prev => (prev + 1) % imageCount);
      }, 3000); // Change image every 3 seconds

      return () => clearInterval(interval);
    }, [hasMultipleImages, workspaceImages.length]);

    return (
      <div
        className={`flex items-center p-2 rounded mb-1 transition-colors group ${
          isSelected ? "bg-gray-100" : "hover:bg-gray-50"
        }`}
      >
        <div
          className="flex items-center flex-1 min-w-0 cursor-pointer"
          onClick={() => {
            onClick(workspace);
            handleWorkspaceSelection(workspace);

            if (window.innerWidth < 768) {
              setShowMobileSidebar(false);
            }
          }}
        >
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <div className="relative overflow-hidden mr-2 w-6 h-6 bg-gray-200 rounded-full flex-shrink-0">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={workspace.name}
                      className="object-cover w-full h-full"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="flex justify-center items-center w-full h-full text-xs font-medium text-gray-600 bg-gray-300">
                      {workspace.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {showCategory && workspace.category && (
                    <div
                      className={`absolute bottom-0 right-0 w-2 h-2 ${categoryInfo.dotColor} rounded-full border border-white`}
                    />
                  )}
                </div>
              </TooltipTrigger>
              {hasMultipleImages && workspaceImages.length > 0 && (
                <TooltipContent
                  side="right"
                  className="p-0 bg-transparent border-0 shadow-none max-w-none"
                  onPointerDownOutside={e => e.preventDefault()}
                >
                  <div className="relative w-64 h-48 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                    <img
                      src={workspaceImages[tooltipImageIndex]}
                      alt={`${workspace.name} ${tooltipImageIndex + 1}`}
                      className="w-full h-full object-cover"
                      onError={e => {
                        e.target.src = "/img/placeholder-workspace.jpg";
                      }}
                    />
                    {/* Image Counter */}
                    <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 text-white text-xs rounded">
                      {tooltipImageIndex + 1} / {workspaceImages.length}
                    </div>
                    {/* Indicators */}
                    {workspaceImages.length > 1 && (
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {workspaceImages.map((_, index) => (
                          <button
                            key={index}
                            onClick={e => {
                              e.stopPropagation();
                              setTooltipImageIndex(index);
                            }}
                            className={`h-1.5 rounded-full transition-all ${
                              index === tooltipImageIndex ? "bg-white w-6" : "bg-white/50 w-1.5"
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <div className="text-xs font-medium text-gray-900 truncate">{workspace.name}</div>
              {showCategory && workspace.category && (
                <span
                  className={`px-1 py-0.5 text-[10px] font-medium rounded border ${categoryInfo.color} flex-shrink-0`}
                >
                  {categoryInfo.label}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 truncate">{workspace.location}</div>
          </div>
        </div>

        {/* 3-dot menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={e => e.stopPropagation()}
              className="p-1 rounded hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            >
              <MoreVertical className="w-4 h-4 text-gray-600" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              onClick={e => {
                e.stopPropagation();
                if (onViewDetails) {
                  onViewDetails(workspace);
                }
              }}
              className="cursor-pointer"
            >
              <Eye className="w-4 h-4 mr-2" />
              See Space Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  // Generate time slots for full 24-hour day
  const generateDynamicTimeSlots = () => {
    const slots = [];
    // Generate all 24 hours (0 = 12 AM to 23 = 11 PM)
    for (let hour = 0; hour < 24; hour++) {
      let timeString;
      if (timeFormat === "24h") {
        timeString = `${hour.toString().padStart(2, "0")}:00`;
      } else {
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour >= 12 ? "PM" : "AM";
        timeString = `${displayHour.toString().padStart(2, "0")}:00 ${period}`;
      }
      slots.push({ time: timeString, hour });
    }

    logger.log(
      "Generated full day time slots:",
      slots.length,
      "slots from",
      slots[0].time,
      "to",
      slots[slots.length - 1].time
    );
    return slots;
  };

  const timeSlots = generateDynamicTimeSlots();

  const ViewModeDropdown = () => {
    const viewOptions = canViewCalendar ? ["Daily", "Monthly", "Yearly", "List"] : ["List"];

    return (
      <div className="flex items-center space-x-2">
        <HDropdown
          label={viewMode}
          options={viewOptions}
          onChange={mode => {
            changeViewMode(mode);
          }}
          variant="default"
          className="text-xs md:text-sm"
        />
        {canCreateBooking && (
          <HActionButton
            label="Create Booking"
            icon={<Plus className="w-4 h-4" />}
            className="text-xs md:text-sm"
            onClick={handleCreateBooking}
          />
        )}
      </div>
    );
  };

  const ListViewComponent = () => {
    const filteredBookings = listBookings;

    const handlePageChange = page => {
      if (page < 1) return;
      setListPage(page);
      fetchListBookings(page, searchQuery, listSort, listStatusFilter);
    };

    if (loading || listLoading) {
      return (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-16 bg-gray-100 rounded animate-pulse mt-1" />
            </div>
            <div className="h-9 w-48 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="flex flex-col gap-1.5">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-100">
                <div className="w-10 h-10 bg-gray-100 rounded-md animate-pulse shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-3/4 bg-gray-100 rounded animate-pulse" />
                  <div className="h-2.5 w-1/3 bg-gray-100 rounded animate-pulse" />
                </div>
                <div className="h-8 w-16 bg-gray-100 rounded-md animate-pulse shrink-0" />
              </div>
            ))}
          </div>
        </div>
      );
    }

    const totalPages = Math.ceil((listTotalCount || filteredBookings.length) / 20);
    const hasActiveFilters = searchQuery || listStatusFilter !== "ALL";

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Bookings</h2>
            <p className="text-sm text-gray-500">{listTotalCount || filteredBookings.length} total</p>
          </div>
          <div className="flex items-center gap-2">
            <SortDropdown options={LIST_SORT_OPTIONS} value={listSort} onChange={setListSort} />
            <DropdownMenu open={showListFilters} onOpenChange={setShowListFilters}>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {listStatusFilter !== "ALL" && (
                    <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">1</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="p-2">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Status</p>
                  {["ALL", "PENDING", "PENDING_PAYMENT", "CHECKED_IN", "COMPLETED", "CANCELED", "NO_SHOW"].map(
                    status => (
                      <button
                        key={status}
                        onClick={() => {
                          setListStatusFilter(status);
                          setShowListFilters(false);
                        }}
                        className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-100 ${
                          listStatusFilter === status ? "bg-gray-100 font-medium" : ""
                        }`}
                      >
                        {status === "ALL"
                          ? "All Statuses"
                          : status
                              .replace("_", " ")
                              .toLowerCase()
                              .replace(/\b\w/g, l => l.toUpperCase())}
                      </button>
                    )
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Empty State or List */}
        {!filteredBookings.length ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 bg-white border border-dashed border-gray-200 rounded-xl">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-800 mb-1">
              {hasActiveFilters ? "No bookings match your filters" : "No bookings yet"}
            </h3>
            <p className="text-sm text-gray-500 text-center max-w-xs">
              {hasActiveFilters
                ? "Try adjusting your search or filters to see more results."
                : "Bookings will appear here once customers reserve your spaces."}
            </p>
          </div>
        ) : (
          <>
            {/* List View */}
            <div className="space-y-2">
              {filteredBookings.map(booking => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  onManage={handleModifyBooking}
                  onCancel={handleCancelBooking}
                  onCheckIn={handleCheckInBooking}
                  onCheckOut={handleCheckOutBooking}
                  formatTimeRange={formatTimeRange}
                  showConfirmation={showConfirmation}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <button
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => handlePageChange(listPage - 1)}
                  disabled={listPage === 1}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500 px-2">
                  Page {listPage} of {totalPages}
                </span>
                <button
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => handlePageChange(listPage + 1)}
                  disabled={listPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const DailyViewComponent = () => {
    const dayBookings = bookings.filter(booking => {
      // Show all bookings including cancelled ones so they don't disappear
      const status = booking.status ? booking.status.toLowerCase() : "";
      // Users can see cancelled bookings but they are non-actionable

      const bookingDate = booking.date;
      const isCorrectDate =
        bookingDate.getDate() === selectedDate.getDate() &&
        bookingDate.getMonth() === selectedDate.getMonth() &&
        bookingDate.getFullYear() === selectedDate.getFullYear();
      const isCorrectWorkspace = selectedWorkspace ? booking.spaceId === selectedWorkspace.id : true;
      return isCorrectDate && isCorrectWorkspace;
    });

    logger.log("Daily view render:", {
      selectedDate: selectedDate.toDateString(),
      selectedWorkspace: selectedWorkspace?.name || "All spaces",
      totalBookings: bookings.length,
      dayBookings: dayBookings.length,
      timeSlots: timeSlots.length,
      workspaces: workspaces.length,
    });

    const getBookingStyle = booking => {
      const startHour = booking.startTime.getHours();
      const endHour = booking.endTime.getHours();
      const startIndex = timeSlots.findIndex(slot => slot.hour === startHour);
      const endIndex = timeSlots.findIndex(slot => slot.hour === endHour);

      if (startIndex === -1) return null;

      const height = endIndex > startIndex ? (endIndex - startIndex) * 60 : 60;
      const top = startIndex * 60;

      return {
        position: "absolute",
        top: `${top}px`,
        height: `${height}px`,
        left: "8px",
        right: "8px",
        zIndex: 10,
      };
    };

    return (
      <div className="flex flex-col flex-1 h-full bg-white">
        {/* Header */}
        <div className="flex flex-shrink-0 justify-between items-center p-3 border-b border-gray-200 md:p-4">
          <div className="flex items-center space-x-3">
            {/* Mobile menu button - Better positioned and styled */}
            <button
              onClick={toggleMobileSidebar}
              className="p-2 rounded-lg border border-gray-200 shadow-sm md:hidden hover:bg-gray-100"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1 md:flex-none">
              <div className="flex justify-center items-center space-x-2 md:justify-start">
                <h2 className="text-sm font-medium md:text-base">
                  {window.innerWidth < 768
                    ? selectedDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    : formatDate(selectedDate)}
                </h2>
                {selectedWorkspace && isDateAvailable(selectedDate) && (
                  <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                    Available
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-center text-gray-500 md:text-left">
                {selectedWorkspace ? (
                  <>
                    {selectedWorkspace.name} - {dayBookings.length} booking
                    {dayBookings.length !== 1 ? "s" : ""}
                  </>
                ) : (
                  <>
                    All spaces - {dayBookings.length} booking
                    {dayBookings.length !== 1 ? "s" : ""}
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2 md:space-x-3">
            <button
              onClick={goToToday}
              className="px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors md:px-3 md:py-1.5 md:text-sm"
            >
              Today
            </button>
            <div className="flex items-center space-x-1">
              <button
                onClick={goToPreviousDay}
                className="p-1.5 md:p-2 hover:bg-gray-100 rounded"
                aria-label="Previous day"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={goToNextDay} className="p-1.5 md:p-2 hover:bg-gray-100 rounded" aria-label="Next day">
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Time slots - Scrollable */}
        <div className="overflow-y-auto flex-1">
          <div className="relative" style={{ height: `${timeSlots.length * 60}px` }}>
            {timeSlots.map((slot, index) => (
              <div
                key={index}
                className="flex absolute w-full border-b border-gray-100"
                style={{
                  top: `${index * 60}px`,
                  height: "60px",
                }}
              >
                <div className="p-2 w-12 text-xs text-right text-gray-500 bg-gray-50 md:w-20 md:p-3">{slot.time}</div>
                <div className="relative flex-1">
                  {/* Grid line */}
                  <div className="absolute inset-0 border-l border-gray-100"></div>

                  {/* Show availability indicator for this time slot - only when specific workspace is selected */}
                  {(() => {
                    // Only show green highlights when a specific workspace is selected
                    if (!selectedWorkspace) return null;

                    const availability = getSpaceAvailabilityForDate(selectedWorkspace, selectedDate);
                    if (!availability || (Array.isArray(availability) && availability.length === 0)) return null;

                    const slotHour = slot.hour;

                    // Check if this hour is within any of the available slots for the day
                    const availabilities = Array.isArray(availability) ? availability : [availability];
                    const hasAvailability = availabilities.some(
                      avail => slotHour >= avail.startHour && slotHour < avail.endHour
                    );

                    // Check if there's a booking in this hour (for buffer time visualization)
                    const slotStart = new Date(selectedDate);
                    slotStart.setHours(slotHour, 0, 0, 0);
                    const slotEnd = new Date(selectedDate);
                    slotEnd.setHours(slotHour + 1, 0, 0, 0);

                    const bufferTime = selectedWorkspace.bufferTimeInMinutes || 0;

                    const hasBooking = dayBookings.some(booking => {
                      const bookingStart = new Date(booking.startTime);
                      const bookingEnd = new Date(booking.endTime);
                      return slotStart < bookingEnd && slotEnd > bookingStart;
                    });

                    // For multi-instance availability highlight
                    const totalInstances = selectedWorkspace.totalInstances || 1;
                    const overlappingBookingsCount = dayBookings.filter(booking => {
                      const bookingStart = new Date(booking.startTime);
                      const bookingEnd = new Date(booking.endTime);
                      return slotStart < bookingEnd && slotEnd > bookingStart;
                    }).length;

                    const isInBufferTime = dayBookings.some(booking => {
                      const bookingEnd = new Date(booking.endTime);
                      const bufferEnd = new Date(bookingEnd);
                      bufferEnd.setMinutes(bufferEnd.getMinutes() + bufferTime);
                      return slotStart >= bookingEnd && slotStart < bufferEnd;
                    });

                    // Multi-instance availability highlight
                    if (hasAvailability && !isInBufferTime) {
                      if (overlappingBookingsCount === 0) {
                        // Fully available
                        return (
                          <div
                            className="absolute inset-0 bg-green-100 opacity-70 pointer-events-none"
                            title={`${selectedWorkspace.name} fully available (${totalInstances} units)`}
                          />
                        );
                      } else if (overlappingBookingsCount < totalInstances) {
                        // Partially available
                        return (
                          <div
                            className="absolute inset-0 bg-orange-100 opacity-70 pointer-events-none"
                            title={`${selectedWorkspace.name} partially available (${totalInstances - overlappingBookingsCount} units left)`}
                          />
                        );
                      }
                    } else if (isInBufferTime && hasAvailability) {
                      return (
                        <div
                          className="absolute inset-0 bg-yellow-100 opacity-70 pointer-events-none"
                          title={`Buffer time (${bufferTime} minutes after booking)`}
                        />
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            ))}

            {/* Render bookings */}
            {loading ? (
              // Loading skeleton for bookings - positioned to avoid time labels
              <>
                {[1, 2, 3].map(i => (
                  <div
                    key={i}
                    className="absolute bg-gray-200 rounded animate-pulse"
                    style={{
                      top: `${(i + 2) * 60 + 10}px`,
                      height: "50px",
                      left: "88px", // Start after the time label area (80px + 8px margin)
                      right: "8px",
                      zIndex: 5,
                    }}
                  >
                    <div className="p-2">
                      <div className="mb-1 w-24 h-3 bg-gray-300 rounded"></div>
                      <div className="w-16 h-2 bg-gray-300 rounded"></div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              dayBookings.map(booking => {
                const style = getBookingStyle(booking);
                if (!style) return null;

                return (
                  <BookingActionsCard
                    key={booking.id}
                    booking={booking}
                    style={style}
                    formatTimeRange={formatTimeRange}
                    onModify={handleModifyBooking}
                    onCancel={handleCancelBooking}
                    onCheckIn={handleCheckInBooking}
                    onCheckOut={handleCheckOutBooking}
                    showConfirmation={showConfirmation}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  const MonthlyViewComponent = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const today = new Date();

    const days = [];
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push({
        day: daysInPrevMonth - firstDayOfMonth + i + 1,
        month: prevMonth,
        year: prevYear,
        isCurrentMonth: false,
        fullDate: new Date(prevYear, prevMonth, daysInPrevMonth - firstDayOfMonth + i + 1),
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        month: month,
        year,
        isCurrentMonth: true,
        fullDate: new Date(year, month, i),
      });
    }

    const remainingSlots = 42 - days.length;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;

    for (let i = 1; i <= remainingSlots; i++) {
      days.push({
        day: i,
        month: nextMonth,
        year: nextYear,
        isCurrentMonth: false,
        fullDate: new Date(nextYear, nextMonth, i),
      });
    }

    const isToday = dayObj => {
      return dayObj.day === today.getDate() && dayObj.month === today.getMonth() && dayObj.year === today.getFullYear();
    };

    const getDayBookings = dayObj => {
      return bookings.filter(booking => {
        // Show all bookings including cancelled ones
        const status = booking.status ? booking.status.toLowerCase() : "";

        const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
        const isCorrectDate =
          bookingDate.getDate() === dayObj.day &&
          bookingDate.getMonth() === dayObj.month &&
          bookingDate.getFullYear() === dayObj.year;
        const isCorrectWorkspace = selectedWorkspace ? booking.spaceId === selectedWorkspace.id : true;
        return isCorrectDate && isCorrectWorkspace;
      });
    };

    return (
      <div className="flex-1 bg-white">
        {/* Header */}
        <div className="flex justify-between items-center p-3 border-b border-gray-200 md:p-4">
          <div className="flex flex-1 items-center space-x-3">
            <button
              onClick={toggleMobileSidebar}
              className="p-2 rounded-lg border border-gray-200 shadow-sm md:hidden hover:bg-gray-100"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1 md:flex-none">
              <h2 className="text-sm font-medium text-center md:text-base md:text-left">
                {currentDate.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </h2>
              <p className="mt-1 text-xs text-center text-gray-500 md:text-left">
                {selectedWorkspace ? selectedWorkspace.name : "All spaces"}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2 md:space-x-3">
            <button
              onClick={goToToday}
              className="px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors md:px-3 md:py-1.5 md:text-sm"
            >
              Today
            </button>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentDate(new Date(year, month - 1))}
                className="p-1.5 md:p-2 hover:bg-gray-100 rounded"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={() => setCurrentDate(new Date(year, month + 1))}
                className="p-1.5 md:p-2 hover:bg-gray-100 rounded"
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Days of week header */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {daysOfWeek.map((day, index) => (
            <div key={index} className="p-2 text-center border-r border-gray-200 md:p-3 last:border-r-0">
              <div className="text-xs font-medium text-gray-900 md:text-sm">
                {window.innerWidth < 768 ? day.substring(0, 1) : day}
              </div>
            </div>
          ))}
        </div>

        {/* Calendar grid - Clean modern design */}
        <div className="grid grid-cols-7 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          {days.map((dayObj, index) => {
            const dayBookings = getDayBookings(dayObj);
            const isCurrentDay = isToday(dayObj);
            const isAvailable = selectedWorkspace && isDateAvailable(dayObj.fullDate) && dayObj.isCurrentMonth;

            return (
              <div
                key={index}
                className={cn(
                  "flex relative flex-col p-2 border-r border-b border-gray-100 last:border-r-0 min-h-[100px] md:min-h-[120px] transition-all",
                  !dayObj.isCurrentMonth && "bg-gray-50/50",
                  isCurrentDay && "bg-blue-50/50 ring-2 ring-blue-500 ring-inset",
                  isAvailable && !isCurrentDay && "bg-green-50/30 hover:bg-green-50/50"
                )}
              >
                {/* Day number */}
                <div className="flex items-start justify-between mb-1">
                  <div
                    className={cn(
                      "text-sm md:text-base font-semibold",
                      !dayObj.isCurrentMonth
                        ? "text-gray-300"
                        : isCurrentDay
                          ? "text-blue-700"
                          : isAvailable
                            ? "text-green-700"
                            : "text-gray-700"
                    )}
                  >
                    {dayObj.day}
                  </div>
                  {/* Available indicator dot */}
                  {isAvailable && !isCurrentDay && <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1"></div>}
                  {isCurrentDay && <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1"></div>}
                </div>

                {/* Multi-instance availability indicators */}
                {selectedWorkspace && selectedWorkspace.totalInstances > 1 && (
                  <div className="flex flex-wrap gap-0.5 mb-2">
                    {Array.from({ length: selectedWorkspace.totalInstances }).map((_, idx) => {
                      const isUnitBooked = idx < dayBookings.length;
                      return (
                        <div
                          key={idx}
                          className={cn("h-1 w-2 rounded-full", isUnitBooked ? "bg-red-400" : "bg-green-400")}
                          title={isUnitBooked ? "Unit booked" : "Unit available"}
                        />
                      );
                    })}
                  </div>
                )}

                {/* Bookings */}
                <div className="flex flex-col gap-1 w-full mt-auto">
                  {loading ? (
                    // Loading skeleton for bookings in monthly view
                    <div className="space-y-1 w-full">
                      <div className="mx-auto w-8 h-2 bg-gray-200 rounded animate-pulse"></div>
                      <div className="mx-auto w-6 h-1 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  ) : (
                    <>
                      {dayBookings.slice(0, window.innerWidth < 768 ? 2 : 2).map((booking, _bookingIndex) => (
                        <div
                          key={booking.id}
                          className="px-1 py-1 w-full max-w-full text-xs font-medium text-center text-white rounded md:px-2"
                          style={{
                            backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                          }}
                        >
                          {window.innerWidth < 768 ? "●" : formatTimeRange(booking.startTime, booking.endTime)}
                        </div>
                      ))}

                      {dayBookings.length > 2 && (
                        <div className="text-xs text-center text-gray-500">+{dayBookings.length - 2}</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const YearlyViewComponent = () => {
    const currentYear = selectedDate.getFullYear();
    const today = new Date();
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const handleViewAllEvents = date => {
      // Set the selected date to the clicked date
      setSelectedDate(date);
      // Switch to daily view
      changeViewMode("Daily");
    };

    const renderSmallCalendar = monthIndex => {
      const year = currentYear;
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
      const firstDay = new Date(year, monthIndex, 1).getDay();
      const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

      const days = [];

      const prevMonth = monthIndex === 0 ? 11 : monthIndex - 1;
      const prevYear = monthIndex === 0 ? year - 1 : year;
      const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

      for (let i = 0; i < firstDay; i++) {
        days.push({
          day: daysInPrevMonth - firstDay + i + 1,
          month: prevMonth,
          year: prevYear,
          isCurrentMonth: false,
        });
      }

      for (let i = 1; i <= daysInMonth; i++) {
        days.push({
          day: i,
          month: monthIndex,
          year,
          isCurrentMonth: true,
        });
      }

      const isToday = dayObj => {
        return (
          dayObj.day === today.getDate() && dayObj.month === today.getMonth() && dayObj.year === today.getFullYear()
        );
      };

      const getDayBookings = dayObj => {
        return bookings.filter(booking => {
          const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
          const isCorrectDate =
            bookingDate.getDate() === dayObj.day &&
            bookingDate.getMonth() === dayObj.month &&
            bookingDate.getFullYear() === dayObj.year;
          const isCorrectWorkspace = selectedWorkspace ? booking.spaceId === selectedWorkspace.id : true;
          return isCorrectDate && isCorrectWorkspace;
        });
      };

      return (
        <div className="p-2 rounded-lg border border-gray-200 md:p-3">
          <h3 className="mb-2 text-xs font-medium text-center">
            {months[monthIndex]} {year}
          </h3>
          <div className="grid grid-cols-7 gap-1 text-xs">
            {daysOfWeek.map((day, index) => (
              <div key={index} className="p-1 text-center text-gray-500">
                {day}
              </div>
            ))}
            {days.slice(0, 35).map((dayObj, index) => {
              const dayBookings = getDayBookings(dayObj);
              const isCurrentDay = isToday(dayObj);
              const hasBookings = dayBookings.length > 0;

              return (
                <HDayPopover
                  key={index}
                  date={new Date(dayObj.year, dayObj.month, dayObj.day)}
                  bookings={dayBookings}
                  formatTimeRange={formatTimeRange}
                  onViewAllEvents={handleViewAllEvents}
                >
                  <button
                    type="button"
                    className={`text-center p-1 relative cursor-pointer rounded transition-colors w-full ${
                      dayObj.isCurrentMonth
                        ? selectedWorkspace && isDateAvailable(new Date(dayObj.year, dayObj.month, dayObj.day))
                          ? "text-green-700 bg-green-50 hover:bg-green-100"
                          : "text-gray-900 hover:bg-gray-100"
                        : "text-gray-300"
                    }`}
                  >
                    {isCurrentDay ? (
                      <div
                        className="flex justify-center items-center mx-auto w-6 h-6 text-xs font-medium text-white rounded-full"
                        style={{
                          backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                        }}
                      >
                        {dayObj.day}
                      </div>
                    ) : (
                      <div
                        className={`flex justify-center items-center mx-auto w-6 h-6 text-xs ${
                          selectedWorkspace &&
                          isDateAvailable(new Date(dayObj.year, dayObj.month, dayObj.day)) &&
                          dayObj.isCurrentMonth
                            ? "font-medium"
                            : ""
                        }`}
                      >
                        {dayObj.day}
                      </div>
                    )}
                    {/* Available day indicator for yearly view */}
                    {selectedWorkspace &&
                      isDateAvailable(new Date(dayObj.year, dayObj.month, dayObj.day)) &&
                      dayObj.isCurrentMonth &&
                      !isCurrentDay &&
                      !hasBookings && (
                        <div className="flex justify-center mt-0.5">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        </div>
                      )}
                    {loading ? (
                      // Loading skeleton for yearly view booking indicators
                      <div className="flex justify-center mt-0.5">
                        <div className="w-3 h-1 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    ) : (
                      hasBookings && (
                        <div className="flex justify-center mt-0.5">
                          <div className="flex space-x-0.5">
                            <div
                              className="w-1 h-1 rounded-full"
                              style={{
                                backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                              }}
                            ></div>
                            <div
                              className="w-1 h-1 rounded-full"
                              style={{
                                backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                              }}
                            ></div>
                            <div
                              className="w-1 h-1 rounded-full"
                              style={{
                                backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                              }}
                            ></div>
                          </div>
                        </div>
                      )
                    )}
                  </button>
                </HDayPopover>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div className="flex flex-col flex-1 h-full bg-white">
        {/* Header */}
        <div className="flex flex-shrink-0 justify-between items-center p-3 border-b border-gray-200 md:p-4">
          <div className="flex flex-1 items-center space-x-3">
            <button
              onClick={toggleMobileSidebar}
              className="p-2 rounded-lg border border-gray-200 shadow-sm md:hidden hover:bg-gray-100"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5 text-gray-700" />
            </button>
            <h2 className="flex-1 text-sm font-medium text-center md:text-base md:text-left">{currentYear}</h2>
          </div>
          <div className="flex items-center space-x-2 md:space-x-3">
            <button
              onClick={goToToday}
              className="px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors md:px-3 md:py-1.5 md:text-sm"
            >
              Today
            </button>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setSelectedDate(new Date(currentYear - 1, 0, 1))}
                className="p-1.5 md:p-2 hover:bg-gray-100 rounded"
                aria-label="Previous year"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={() => setSelectedDate(new Date(currentYear + 1, 0, 1))}
                className="p-1.5 md:p-2 hover:bg-gray-100 rounded"
                aria-label="Next year"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable yearly calendar grid - Better mobile centering */}
        <div className="overflow-y-auto flex-1 p-3 md:p-4">
          <div className="grid grid-cols-1 gap-3 mx-auto max-w-6xl sm:grid-cols-2 md:grid-cols-3 md:gap-4">
            {months.map((_, index) => renderSmallCalendar(index))}
          </div>
        </div>
      </div>
    );
  };

  const renderMainContent = () => {
    switch (viewMode) {
      case "Daily":
        return <DailyViewComponent />;
      case "Monthly":
        return <MonthlyViewComponent />;
      case "Yearly":
        return <YearlyViewComponent />;
      case "Hourly":
        return <DailyViewComponent />; // Hourly uses Daily view
      case "List":
        return <ListViewComponent />;
      default:
        return <ListViewComponent />;
    }
  };

  // Remove the loading state shimmer - show actual calendar instead

  return (
    <>
      <HPageContainer>
        <HHeader rightButton={<ViewModeDropdown />} />

        <div className="flex overflow-hidden relative flex-1 p-3 h-full sm:p-4 md:p-4 lg:p-4">
          {/* Mobile Sidebar Overlay */}
          {showMobileSidebar && (
            <div
              className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
              onClick={() => setShowMobileSidebar(false)}
            />
          )}

          {/* Left Sidebar */}
          <div
            className={`
                ${showMobileSidebar ? "translate-x-0" : "-translate-x-full"}
                md:translate-x-0 fixed md:relative z-50 md:z-auto
                w-80 md:w-64 bg-white rounded-lg p-3 flex flex-col
                transition-transform duration-300 ease-in-out
                h-full md:h-auto inset-y-0 left-0
            `}
          >
            {/* Mobile close button */}
            <div className="flex justify-between items-center mb-4 md:hidden">
              <h3 className="text-lg font-semibold">Menu</h3>
              <button onClick={() => setShowMobileSidebar(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mini Calendar */}
            <MiniCalendar />

            {/* Toggle - Hidden for now */}
            {/* <HToggle
            leftLabel='Meeting Rooms'
            rightLabel='Workspace'
            isToggled={workspaceToggle}
            onToggle={() => setWorkspaceToggle(!workspaceToggle)}
            className='mb-4'
          /> */}

            {/* Search */}
            <div className="relative mb-3">
              <Input
                type="text"
                placeholder="Search spaces"
                className="pr-8 text-sm"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <Search className="absolute right-3 top-2.5 h-3 w-3 text-gray-400 pointer-events-none" />
            </div>

            {/* Category Filter */}
            <div className="mb-3">
              <div className="mb-1.5">
                <h4 className="text-xs font-medium tracking-wide text-gray-600">Category</h4>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {["WORK", "STAY", "PLAY"].map(cat => {
                  const categoryInfo = getCategoryInfo(cat);
                  const isActive = categoryFilter === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => {
                        // Toggle: if clicking the same category, deselect it
                        setCategoryFilter(isActive ? null : cat);
                      }}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors border ${
                        isActive
                          ? `${categoryInfo.color} border-2`
                          : "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200"
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Clear Filter Button - More compact */}
            {selectedWorkspace && (
              <div className="mb-2">
                <button
                  onClick={() => setSelectedWorkspace(null)}
                  className="px-2 py-1 w-full text-xs text-gray-600 bg-gray-100 rounded transition-colors hover:bg-gray-200"
                >
                  ✕ Clear Space Filter
                </button>
              </div>
            )}

            {/* Workspace List */}
            <div className="overflow-auto flex-1">
              <div className="mb-1">
                <h4 className="text-xs font-medium tracking-wide text-gray-600">Available spaces</h4>
              </div>
              {loading ? (
                // Loading skeleton for workspace list - more compact
                <div className="space-y-1">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="flex items-center p-1.5 rounded animate-pulse">
                      <div className="mr-2 w-6 h-6 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="mb-0.5 w-16 h-2.5 bg-gray-200 rounded"></div>
                        <div className="w-12 h-2 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                (() => {
                  // Filter workspaces by search and category
                  const filteredWorkspaces = workspaces.filter(w => {
                    const matchesSearch = w.name.toLowerCase().includes(searchQuery.toLowerCase());
                    const workspaceCategory = normalizeCategory(w.category);
                    // If categoryFilter is null, show all; otherwise filter by category
                    const matchesCategory = categoryFilter === null || workspaceCategory === categoryFilter;
                    return matchesSearch && matchesCategory;
                  });

                  // Group by category
                  const groupedByCategory = filteredWorkspaces.reduce((acc, workspace) => {
                    const category = normalizeCategory(workspace.category) || "OTHER";
                    if (!acc[category]) {
                      acc[category] = [];
                    }
                    acc[category].push(workspace);
                    return acc;
                  }, {});

                  // Order categories: WORK, STAY, PLAY, OTHER
                  const categoryOrder = ["WORK", "STAY", "PLAY", "OTHER"];

                  if (filteredWorkspaces.length === 0) {
                    return (
                      <div className="p-2 text-center text-gray-500">
                        <div className="text-xs">No spaces found</div>
                        {(searchQuery || categoryFilter !== null) && (
                          <div className="text-xs">Try adjusting your filters</div>
                        )}
                      </div>
                    );
                  }

                  return categoryOrder.map(category => {
                    const categoryWorkspaces = groupedByCategory[category] || [];
                    if (categoryWorkspaces.length === 0) return null;

                    const categoryInfo = getCategoryInfo(category);

                    return (
                      <div key={category} className="mb-1">
                        {/* Category Divider */}
                        <div className="flex items-center gap-2 mb-1 px-1">
                          <div className={`w-1 h-4 ${categoryInfo.dotColor} rounded-full`} />
                          <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            {category} ({categoryWorkspaces.length})
                          </h5>
                        </div>
                        {/* Workspaces in this category */}
                        {categoryWorkspaces.map(workspace => (
                          <WorkspaceItem
                            key={workspace.id}
                            workspace={workspace}
                            isSelected={selectedWorkspace?.id === workspace.id}
                            onClick={setSelectedWorkspace}
                            showCategory={false}
                            onViewDetails={space => {
                              setSelectedSpaceForDetails(space);
                              setShowSpaceDetailsModal(true);
                            }}
                          />
                        ))}
                      </div>
                    );
                  });
                })()
              )}
            </div>
          </div>

          {/* Main Content Area - Better mobile spacing */}
          <div className="overflow-y-auto flex-1 rounded-lg md:ml-4">
            {!canViewCalendar && FeatureAccess.canUpgradePlan(currentUser) && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 px-5 py-4 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Unlock Calendar View</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Get a visual overview of all your bookings with our calendar feature.
                  </p>
                </div>
                <HUpgradeButton onClick={goToUpgrade} />
              </div>
            )}
            {renderMainContent()}
          </div>
        </div>

        {/* Unified Booking Modal - handles both create and modify */}
        {/* Unified Booking Modal - handles both create and modify */}
        {/* Render ReusableBookingModal for Creating Bookings */}
        {!isEditMode && showBookingModal && (
          <ReusableBookingModal
            isOpen={showBookingModal}
            onClose={handleBookingModalClose}
            space={selectedWorkspace} // Main space prop
            selectedDate={selectedDate}
            availableTimeSlots={availableTimeSlotsForModal}
            onDateChange={setSelectedDate}
            // Host Calendar specific props
            isHostCalendarMode={true}
            selectedWorkspace={selectedWorkspace}
            workspaces={workspaces}
            onWorkspaceSelect={setSelectedWorkspace}
            // Data
            existingBookings={modalExistingBookings}
            // Config
            userCurrency={userCurrency}
            timeFormat={timeFormat}
            showBookingSummary={true}
            showPaymentOptions={true}
            canBookForCustomer={canBookForCustomer}
            // Handlers
            onBookingSuccess={handleBookingSuccess}
            onBookingError={handleBookingError}
          />
        )}

        {/* Render ModifyBookingModal for Editing Bookings */}
        {isEditMode && showBookingModal && (
          <ModifyBookingModal
            isOpen={showBookingModal}
            onClose={handleModifyModalClose}
            booking={currentBooking}
            selectedWorkspace={selectedWorkspace}
            workspaces={workspaces}
            onUpdateBooking={handleUpdateBookingFromModal}
            availableTimeSlots={availableTimeSlotsForModal}
            existingBookings={modalExistingBookings}
            timeFormat={timeFormat}
            canBookForCustomer={canBookForCustomer}
          />
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => {
            setShowConfirmationModal(false);
            setBookingForConfirmation(null);
            setConfirmationAction(null);
          }}
          onConfirm={() => {
            if (confirmationAction === "check-in") {
              handleConfirmCheckIn();
            } else if (confirmationAction === "check-out") {
              handleConfirmCheckOut();
            } else if (confirmationAction === "cancel") {
              handleConfirmCancel();
            }
          }}
          action={confirmationAction}
          booking={bookingForConfirmation}
          isProcessing={false}
        />

        {/* Space Details Modal */}
        <SpaceDetailsModal
          isOpen={showSpaceDetailsModal}
          onClose={() => {
            setShowSpaceDetailsModal(false);
            setSelectedSpaceForDetails(null);
          }}
          space={selectedSpaceForDetails}
        />
      </HPageContainer>
    </>
  );
};

export default HostCalendar;
